import { z } from 'zod';
import { useLoginAdminStore } from '@/features/login-admin/store';
import { useLoginStore } from '@/features/login/store';
import { getEmpresaIdpk } from '@/features/login/util';
import type { FetchMethod, Response, ResponseOk } from './types';
import { type BuildUrlParams, buildUrl } from './buildUrl';
import { TooManyRequestsError } from './httpError';
import { adminTokenInvalid, catalogoTokenInvalid, getToken, userTokenInvalid } from './token';
import { API_URL } from './url';
import { getUserSessionSignal } from './userSessionSignal';
import { getRouteType, type RouteType } from './util';

export async function fetchApi<
    Params extends BuildUrlParams,
    Body extends Record<string, unknown> | Record<string, unknown>[],
>(props: {
    token?: boolean;
    empresaIdpk?: boolean;
    endpoint: string;
    method: FetchMethod;
    idpk?: number | string;
    params?: Params;
    body?: Body;
    timeout?: number;
    schema: null;
}): Promise<ResponseOk>;

export async function fetchApi<
    Params extends BuildUrlParams,
    Body extends Record<string, unknown> | Record<string, unknown>[],
    Schema extends z.ZodType,
>(props: {
    token?: boolean;
    empresaIdpk?: boolean;
    endpoint: string;
    method: FetchMethod;
    idpk?: number | string;
    params?: Params;
    body?: Body;
    timeout?: number;
    schema: Schema;
}): Promise<z.infer<Schema>>;

export async function fetchApi({
    token = true,
    empresaIdpk = true,
    endpoint,
    method,
    idpk,
    params,
    body,
    timeout,
    schema,
}: {
    token?: boolean;
    empresaIdpk?: boolean;
    endpoint: string;
    method: FetchMethod;
    idpk?: number | string;
    params?: BuildUrlParams;
    body?: Record<string, unknown> | Record<string, unknown>[];
    timeout?: number;
    schema: z.ZodType | null;
}): Promise<unknown> {
    const routeType = getRouteType();
    const isUserRequest = token && routeType === 'user';

    if (isUserRequest && !useLoginStore.getState().login?.token) {
        throw new DOMException('Sessão encerrada', 'AbortError');
    }

    if (
        token &&
        (useLoginStore.getState().renovandoToken || useLoginAdminStore.getState().renovandoToken)
    ) {
        throw new Error('Renovando token');
    }

    const sessionSignal = isUserRequest ? getUserSessionSignal() : undefined;
    const timeoutSignal = timeout ? AbortSignal.timeout(timeout) : undefined;
    const signal =
        sessionSignal && timeoutSignal
            ? AbortSignal.any([sessionSignal, timeoutSignal])
            : (sessionSignal ?? timeoutSignal);

    const init: RequestInit = {
        body: body ? JSON.stringify(body) : undefined,
        headers: {
            ...(token && { Authorization: getToken(routeType) }),
            'Content-Type': 'application/json',
        },
        method,
        signal,
    };

    const innerParams = { ...params };

    if (empresaIdpk && !innerParams.empresa_idpk) {
        const empresaIdpk = getEmpresaIdpk();

        const isVitest = import.meta.env.MODE === 'test' || import.meta.env.VITEST;

        if (!empresaIdpk && !isVitest) {
            throw new Error('Empresa não encontrada');
        }

        if (empresaIdpk) {
            innerParams.empresa_idpk = empresaIdpk;
        }
    }

    const url = `${API_URL}/${buildUrl(endpoint, innerParams, idpk)}`;
    const response = await fetch(url, init);
    signal?.throwIfAborted();
    checkStatus(response, routeType, token);
    const data = await response.json();
    signal?.throwIfAborted();
    throwIfDataInvalid(data, method);

    if (schema) {
        const result = schema.safeParse(data);

        if (result.error) {
            const pretty = z.prettifyError(result.error);

            console.error('fetchApi', {
                error: result.error,
                pretty,
                url,
            });

            throw new Error(pretty);
        }

        return result.data;
    }

    return data;
}

function throwIfDataInvalidRegistros(registros: unknown[]): void {
    for (const registro of registros) {
        if (
            typeof registro === 'object' &&
            registro &&
            'status' in registro &&
            registro.status === 'erro'
        ) {
            if ('mensagem' in registro && typeof registro.mensagem === 'string') {
                throw new Error(registro.mensagem);
            }

            if ('message' in registro && typeof registro.message === 'string') {
                throw new Error(registro.message);
            }

            throw new Error('Erro desconhecido ao processar registro');
        }
    }
}

function throwIfDataInvalid(data: Response, method: FetchMethod): void {
    if (!data) {
        throw new Error('Servidor não retornou dados');
    }

    if (data.status === 'erro') {
        const error = data.mensagem || data.mensagem_original || 'Erro desconhecido';
        throw new Error(error.replaceAll("'", ''), { cause: data });
    }

    if (method === 'post' && Array.isArray(data.registros)) {
        throwIfDataInvalidRegistros(data.registros);
    }
}

function checkStatus(response: globalThis.Response, routeType: RouteType, token: boolean): void {
    if (response.status === 429) {
        throw new TooManyRequestsError();
    }

    if (response.status === 401) {
        if (!token) {
            throw new Error('Token inválido');
        }

        if (routeType === 'admin') {
            adminTokenInvalid();
        } else if (routeType === 'catalogo') {
            catalogoTokenInvalid();
        } else {
            userTokenInvalid();
        }
    }
}
